import i18next from 'i18next';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

import { getTheme } from '@commons/utils/theme';
import EmptyState from '@commons/EmptyState';

import { buildPerformanceRows } from '../../utils/buildPerformanceRows';

import PerformanceRow from './components/PerformanceRow';

import { Container, EmptyStateContainer, RowsList } from './styledComponents';

const PerformancesContent = (props) => {
  const {
    associatedProduct,
    clientChannels,
    compositions,
    currency,
    defaultChannelId,
    displayMode,
    emptyStateLabel,
    isEmptyState,
    lastChangedFilter,
    mainRecipeOptionId,
    recipe,
    recipeVariants,
    selectedChannels,
    selectedStores,
    selectedVariants,
    stores,
  } = props;

  const theme = getTheme();
  const emptyStateBackgroundColor = theme.colors.greys.lightest;

  const mainRecipeName = recipe?.name ?? '';

  const rows = useMemo(() => {
    if (isEmptyState || !defaultChannelId) {
      return [];
    }

    return buildPerformanceRows({
      lastChangedFilter,
      selectedVariants,
      selectedStores,
      recipeVariants,
      compositions,
      stores,
      recipe,
      associatedProduct,
      channels: clientChannels,
      defaultChannelId,
      mainRecipeOptionId,
      mainRecipeName,
    });
  }, [
    isEmptyState,
    defaultChannelId,
    lastChangedFilter,
    selectedVariants,
    selectedStores,
    recipeVariants,
    compositions,
    stores,
    recipe,
    associatedProduct,
    clientChannels,
    mainRecipeOptionId,
    mainRecipeName,
  ]);

  const maxPriceExTax = useMemo(() => {
    let max = null;

    rows.forEach((row) => {
      selectedChannels.forEach((channel) => {
        const price = row.byChannel?.[channel.id]?.priceExTax;

        if (typeof price === 'number' && Number.isFinite(price) && (max === null || price > max)) {
          max = price;
        }
      });
    });

    return max;
  }, [rows, selectedChannels]);

  if (isEmptyState) {
    return (
      <Container>
        <EmptyStateContainer>
          <EmptyState backgroundColor={emptyStateBackgroundColor} label={emptyStateLabel} />
        </EmptyStateContainer>
      </Container>
    );
  }

  if (rows.length === 0 || selectedChannels.length === 0) {
    return (
      <Container>
        <EmptyStateContainer>
          <EmptyState
            backgroundColor={emptyStateBackgroundColor}
            label={i18next.t('ADMIN.RECIPES.PERFORMANCES_EMPTY_STATE')}
          />
        </EmptyStateContainer>
      </Container>
    );
  }

  return (
    <Container>
      <RowsList>
        {rows.map((row) => (
          <PerformanceRow
            channels={selectedChannels}
            currency={currency}
            displayMode={displayMode}
            key={row.id}
            maxPriceExTax={maxPriceExTax}
            row={row}
          />
        ))}
      </RowsList>
    </Container>
  );
};

PerformancesContent.propTypes = {
  associatedProduct: PropTypes.object,
  clientChannels: PropTypes.arrayOf(PropTypes.object),
  compositions: PropTypes.arrayOf(PropTypes.object),
  currency: PropTypes.object,
  defaultChannelId: PropTypes.string,
  displayMode: PropTypes.string.isRequired,
  emptyStateLabel: PropTypes.string,
  isEmptyState: PropTypes.bool,
  lastChangedFilter: PropTypes.oneOf(['variant', 'store', null]),
  mainRecipeOptionId: PropTypes.string.isRequired,
  recipe: PropTypes.object,
  recipeVariants: PropTypes.arrayOf(PropTypes.object),
  selectedChannels: PropTypes.arrayOf(PropTypes.object),
  selectedStores: PropTypes.arrayOf(PropTypes.object),
  selectedVariants: PropTypes.arrayOf(PropTypes.object),
  stores: PropTypes.arrayOf(PropTypes.object),
};

PerformancesContent.defaultProps = {
  associatedProduct: {},
  clientChannels: [],
  compositions: [],
  currency: null,
  defaultChannelId: null,
  emptyStateLabel: '',
  isEmptyState: false,
  lastChangedFilter: null,
  recipe: {},
  recipeVariants: [],
  selectedChannels: [],
  selectedStores: [],
  selectedVariants: [],
  stores: [],
};

export default PerformancesContent;
